import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Title, Grid, Card, Text, Group, Button, Loader, Stack } from '@mantine/core';
import { IconPackage, IconShoppingCart, IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/admin/AdminLayout';
import { getAllProductsAdmin } from '@/lib/admin/products';
import { Product } from '@/types';

export default function AdminDashboardPage() {
  const { user, isAdmin: isAdminUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || !isAdminUser)) {
      navigate('/xyz/login');
      return;
    }

    if (user && isAdminUser) {
      loadProducts();
    }
  }, [user, isAdminUser, authLoading, navigate]);

  const loadProducts = async () => {
    try {
      const allProducts = await getAllProductsAdmin();
      setProducts(allProducts);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <Container size="xl" py="xl">
          <Loader size="lg" style={{ display: 'block', margin: '0 auto' }} />
        </Container>
      </AdminLayout>
    );
  }

  if (!user || !isAdminUser) {
    return null;
  }

  const totalProducts = products.length;
  const inStockProducts = products.filter(p => p.inStock).length;
  const outOfStockProducts = products.filter(p => !p.inStock).length;
  const lowStockProducts = products.filter(p => p.inStock && (p.stockQuantity || 0) < 10).length;

  return (
    <AdminLayout>
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Group justify="space-between" align="center">
            <Title order={1}>Admin Dashboard</Title>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate('/xyz/products/new')}
            >
              Add New Product
            </Button>
          </Group>

          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      Total Products
                    </Text>
                    <Text size="xl" fw={700}>
                      {totalProducts}
                    </Text>
                  </div>
                  <IconPackage size={32} color="#228be6" />
                </Group>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      In Stock
                    </Text>
                    <Text size="xl" fw={700} c="green">
                      {inStockProducts}
                    </Text>
                  </div>
                  <IconPackage size={32} color="#51cf66" />
                </Group>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      Out of Stock
                    </Text>
                    <Text size="xl" fw={700} c="red">
                      {outOfStockProducts}
                    </Text>
                  </div>
                  <IconShoppingCart size={32} color="#fa5252" />
                </Group>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      Low Stock Alert
                    </Text>
                    <Text size="xl" fw={700} c={lowStockProducts > 0 ? 'orange' : 'gray'}>
                      {lowStockProducts}
                    </Text>
                  </div>
                  <IconAlertCircle size={32} color={lowStockProducts > 0 ? '#ffc107' : '#868e96'} />
                </Group>
              </Card>
            </Grid.Col>
          </Grid>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">Quick Actions</Title>
            <Group>
              <Button
                variant="light"
                onClick={() => navigate('/xyz/products')}
              >
                Manage Products
              </Button>
              <Button
                variant="light"
                onClick={() => navigate('/xyz/orders')}
              >
                View Orders
              </Button>
              <Button
                variant="light"
                onClick={() => navigate('/xyz/products/new')}
              >
                Add New Product
              </Button>
            </Group>
          </Card>
        </Stack>
      </Container>
    </AdminLayout>
  );
}
